import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CheckCircle2, Truck, Package, MessageCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchOrder } from '@/lib/api';
import { useApp } from '@/state/AppContext';
import { inr } from '@/lib/format';
import { Footer } from '@/components/Footer';

const OrderConfirmationPage = () => {
  const { orderId } = useParams();
  const { config } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder(orderId).then(setOrder).catch(() => {}).finally(() => setLoading(false));
  }, [orderId]);

  if (loading) {
    return <div className="max-w-3xl mx-auto px-4 py-10"><div className="animate-pulse h-40 rounded-2xl bg-[var(--bb-sky-soft)]" /></div>;
  }
  if (!order) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10 text-center">
        <h1 className="font-display text-2xl font-bold">Order not found</h1>
        <Button asChild className="mt-4 h-11 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white"><Link to="/">Back home</Link></Button>
      </div>
    );
  }

  const waMsg = encodeURIComponent(`Hi Big Bash, I just placed order ${order.order_id}!`);

  return (
    <div className="pb-16" data-testid="order-confirmation-page">
      <section className="bb-cloud-bg">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-white bb-shadow-soft flex items-center justify-center bb-anim-pop">
            <CheckCircle2 className="w-8 h-8 text-[var(--bb-pink)]" />
          </div>
          <h1 className="font-display text-3xl font-bold mt-3">Order placed!</h1>
          <p className="text-sm text-muted-foreground mt-1">A confirmation has been recorded. Save your Order ID to track it later.</p>
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-border">
            <Sparkles className="w-4 h-4 text-[var(--bb-pink)]" />
            <span className="text-sm">Order ID:</span>
            <span className="text-sm font-bold" data-testid="order-confirmation-order-id">{order.order_id}</span>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 md:px-6 -mt-4">
        <div className="rounded-2xl bg-white border border-border p-4 bb-shadow-soft">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--bb-sky-soft)] flex items-center justify-center"><Package className="w-5 h-5 text-[var(--bb-pink-deep)]" /></div>
            <div>
              <div className="text-sm font-semibold">Status: {order.status}</div>
              <div className="text-xs text-muted-foreground">Placed on {new Date(order.created_at).toLocaleString()}</div>
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {order.items.map((it, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <img src={it.customization?.preview_data_url || it.image} alt={it.name} className="w-14 h-14 rounded-lg object-cover bg-[var(--bb-sky-soft)]" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">{it.name}</div>
                  <div className="text-xs text-muted-foreground">Qty {it.qty}{it.customization ? ' · Custom design' : ''}</div>
                </div>
                <div className="text-sm font-semibold">{inr(it.price * it.qty)}</div>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-4 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{inr(order.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Shipping</span><span>{order.shipping === 0 ? 'FREE' : inr(order.shipping)}</span></div>
            <div className="flex justify-between font-bold pt-1"><span>Total</span><span>{inr(order.total)}</span></div>
          </div>
          <div className="border-t border-border mt-3 pt-3 text-xs text-muted-foreground">
            Delivering to <span className="text-foreground font-semibold">{order.address.full_name}</span>, {order.address.line1}{order.address.line2 ? `, ${order.address.line2}` : ''}, {order.address.city}, {order.address.state} - {order.address.pincode}.
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Button asChild className="flex-1 h-12 rounded-xl bg-[var(--bb-pink)] hover:bg-[var(--bb-pink-deep)] text-white" data-testid="order-confirmation-track-order-button">
            <Link to={`/profile?order_id=${order.order_id}&email=${encodeURIComponent(order.address.email)}`}>
              <Truck className="w-4 h-4 mr-1.5" /> Track this order
            </Link>
          </Button>
          <a href={`https://wa.me/${config.whatsapp_number}?text=${waMsg}`} target="_blank" rel="noreferrer" className="flex-1">
            <Button variant="outline" className="w-full h-12 rounded-xl">
              <MessageCircle className="w-4 h-4 mr-1.5" /> Share on WhatsApp
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OrderConfirmationPage;
